'use client';
import { useCallback, useEffect, useState } from 'react';

/* ============================================================
   useLeagueRoute — the league_id lives in the URL (§ shareable links).

   The whole growth story is: someone opens the board on a TV, drops the
   link in the league group chat, and ten people open it. That only works
   if the league is IN the URL:

       /board/1249850335309070336

   So the URL is the source of truth, and localStorage is only a fallback
   for "I came back to the bare homepage" -- never the other way around.
   A link must always win over whatever the visitor happened to open last,
   or you get the worst possible bug: your friend sends you their league
   and you see your own.

   Precedence: explicit prop > URL path > ?league= query > localStorage.

   No router dependency -- this reads location and uses the History API, so
   it works the same in Next.js, Vite, or a plain static export. Navigation
   is pushState (so Back works) plus a popstate listener.
   ============================================================ */

const STORAGE_KEY = 'solari.sleeper.leagueId';

// Sleeper usernames: letters, digits, underscore, period. Dots and
// underscores are safe unencoded in a hash fragment, so no escaping needed.
// Case-insensitive on Sleeper's side -- we lowercase before lookup.
const USERNAME_RE = /^[a-zA-Z0-9._]{1,32}$/;

export function isLeagueId(s) {
  return /^\d{6,}$/.test(String(s || '').trim());
}

export function isUsername(s) {
  const v = String(s || '').trim();
  // a bare number is a league id, never a username
  return USERNAME_RE.test(v) && !/^\d+$/.test(v);
}

// Everything after the '#'. The board is hash-routed (a static CRA build
// 404s on a real path refresh), so the league lives in the fragment, not
// the pathname -- but we read BOTH so old /board/<id> links still resolve.
function segmentsOf(url) {
  const hash = (url.hash || '').replace(/^#/, '');
  const hashPath = hash.split('?')[0];
  return {
    hash: hashPath.split('/').filter(Boolean),
    path: url.pathname.split('/').filter(Boolean),
  };
}

// Sleeper league IDs are long numeric strings. Accept them from the hash
// (#board/<id>), a path segment (/board/<id>), or a ?league= param.
export function leagueIdFromUrl(href) {
  if (!href) return null;
  let url;
  try { url = new URL(href); } catch { return null; }

  // ?league= can live in the real query string OR inside the hash
  // (#board?league=123) -- URL.searchParams only sees the former.
  const hashQ = (url.hash || '').split('?')[1];
  const params = [url.searchParams, new URLSearchParams(hashQ || '')];
  for (const p of params) {
    const q = p.get('league') || p.get('league_id');
    if (q && isLeagueId(q)) return q.trim();
  }

  const { hash, path } = segmentsOf(url);
  for (const segs of [hash, path]) {
    for (let i = segs.length - 1; i >= 0; i--) {
      if (isLeagueId(segs[i])) return segs[i];
    }
  }
  return null;
}

// #sleeper/<username> -- the memorable entry point. Deliberately NOT the
// shareable link: a username can map to several leagues, so this route
// lands on the picker with the lookup already run, and the league the user
// picks then rewrites the URL to the unambiguous #board/<id>.
//
// The platform is in the path on purpose. '#b' saved two characters and
// cost the ability to say the URL out loud; '/sleeper/' also leaves room
// for '/espn/' and '/yahoo/' later without retrofitting a scheme.
export function usernameFromUrl(href) {
  if (!href) return null;
  let url;
  try { url = new URL(href); } catch { return null; }

  const { hash, path } = segmentsOf(url);
  for (const segs of [hash, path]) {
    const i = segs.findIndex((x) => x.toLowerCase() === 'sleeper');
    if (i >= 0 && segs[i + 1] && isUsername(segs[i + 1])) {
      return segs[i + 1].toLowerCase();
    }
  }
  return null;
}

export function boardPath(leagueId, base = '#board') {
  return `${base}/${leagueId}`;
}

export function sleeperPath(username) {
  return `#sleeper/${String(username || '').toLowerCase()}`;
}

export default function useLeagueRoute({ initial = null, base = '#board' } = {}) {
  const [leagueId, setLeagueIdState] = useState(initial);
  const [username, setUsername] = useState(null);
  const [ready, setReady] = useState(false);

  // resolve once on mount, then follow Back/Forward and hash edits
  useEffect(() => {
    const read = () => {
      const href = window.location.href;
      const id = leagueIdFromUrl(href);
      const user = usernameFromUrl(href);
      setUsername(user);
      // An explicit league in the URL always wins. A username is only an
      // entry point, so it must NOT fall back to localStorage -- someone
      // opening /#sleeper/theirname should see the picker for THAT name,
      // not silently get dropped into whatever league we saw last.
      if (id) return id;
      if (user) return null;
      try { return window.localStorage.getItem(STORAGE_KEY); } catch { return null; }
    };

    if (!initial) setLeagueIdState(read());
    else setUsername(usernameFromUrl(window.location.href));
    setReady(true);

    const onNav = () => setLeagueIdState(read());
    window.addEventListener('popstate', onNav);
    window.addEventListener('hashchange', onNav);
    return () => {
      window.removeEventListener('popstate', onNav);
      window.removeEventListener('hashchange', onNav);
    };
  }, [initial]);

  // remember the last league, but only as a fallback for a bare URL
  useEffect(() => {
    if (!leagueId) return;
    try { window.localStorage.setItem(STORAGE_KEY, leagueId); } catch { /* private mode */ }
  }, [leagueId]);

  const setLeagueId = useCallback((id) => {
    setLeagueIdState(id);
    if (typeof window === 'undefined') return;
    if (id) {
      // Picking a league replaces #sleeper/<name> with the canonical
      // #board/<id>, so what the user copies out of the address bar is
      // always the unambiguous shareable link.
      setUsername(null);
      window.history.pushState({ leagueId: id }, '', boardPath(id, base));
    } else {
      try { window.localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
      window.history.pushState({}, '', base);
    }
  }, [base]);

  const shareUrl = leagueId && typeof window !== 'undefined'
    ? `${window.location.origin}/${boardPath(leagueId, base)}`
    : null;

  return { leagueId, setLeagueId, username, ready, shareUrl };
}
